import { BaseElementWithoutShadowDOM } from '@theme/base-element'
import '@theme/ui-cart-note'
import '@theme/ui-cart-remove-toggle'
import '@theme/ui-cart-update-button'
import '@theme/ui-quantity-input'
import firstFocusableElement from '@theme/util-first-focusable-element'
import HTMLUpdateUtility from '@theme/mixin-html-update-utility'
import { WithShopifyCartClientMixin } from '@theme/mixin-with-shopify-cart'

const cartSpecialSelector = '.cart-special'
const cartSpecialsWrapperSelector = '.cart-specials'

export class CartSections extends WithShopifyCartClientMixin(
  BaseElementWithoutShadowDOM
) {
  constructor() {
    super(...arguments)
    this.$htmlUpdateUtility = new HTMLUpdateUtility(this)
    this.tiers = []
  }
  static get properties() {
    return {
      sectionId: { type: String, attribute: 'section-id' },
      tiers: { type: Array, attribute: 'tiers' },
    }
  }

  static SECTIONS_CACHE = {}

  connectedCallback() {
    super.connectedCallback()

    this.handleCartTiers = this.handleCartTiers.bind(this)
    this.setupCartSpecials = this.setupCartSpecials.bind(this)
    this.removeSpecial = this.removeSpecial.bind(this)

    this.$htmlUpdateUtility.addPostProcessCallback(() => {
      window?.Shopify?.PaymentButton?.init()

      if (this.lastFocusedElementSelectorId) {
        setTimeout(() => {
          const activeElement = document.querySelector(
            `#${this.lastFocusedElementSelectorId}`
          )
          if (activeElement) {
            activeElement?.focus()
          } else {
            firstFocusableElement(this)?.focus()
          }

          this.lastFocusedElementSelectorId = null
        }, 50)
      }
    })

    this.tiers?.length && this.handleCartTiers()
    this.setupCartSpecials()
  }

  setupCartSpecials() {
    const removedCartSpecials = sessionStorage.getItem('removedCartSpecials')

    const cartSpecials = this.querySelectorAll(cartSpecialSelector)

    for (const cartSpecial of Array.from(cartSpecials)) {
      const productId = cartSpecial?.dataset.productId || ''

      if (removedCartSpecials?.includes(productId)) {
        cartSpecial.remove()
        continue
      }

      const removeSpecialButton =
        cartSpecial.querySelector('.js-remove-special')

      if (removeSpecialButton) {
        removeSpecialButton.addEventListener('click', () => {
          productId && this.removeSpecial(productId)
        })
      }
    }

    this.recountCartSpecials()
  }

  async handleCartTiers() {
    const cartTotal = this.cart.items.reduce((total, item) => {
      if (
        item.properties &&
        (item.properties['_is_gwp_sample'] === true ||
          item.properties['_is_gwp_gift'] === true)
      ) {
        return total
      }
      return total + item.line_price
    }, 0)

    const productsToRemove = []
    this.tiers.forEach((tier) => {
      if (cartTotal < tier.threshold) {
        if (tier.is_shipping) return

        if (tier.is_free_sample) {
          const freeSampleItem = this.cart.items.find(
            (item) =>
              item.properties && item.properties['_is_gwp_sample'] === true
          )

          if (freeSampleItem) {
            productsToRemove.push(freeSampleItem.key)
          }
        }
        if (!tier.is_free_sample) {
          const freeGiftItem = this.cart.items.find(
            (item) =>
              item.properties && item.properties['_is_gwp_gift'] === true
          )

          if (freeGiftItem) {
            productsToRemove.push(freeGiftItem.key)
          }
        }
      }
    })
    productsToRemove.length && this.removeItemFromCart(productsToRemove)
  }

  removeSpecial(productId) {
    const cartSpecials = this.querySelectorAll(cartSpecialSelector)

    const cartSpecial = Array.from(cartSpecials).find(
      (cartSpecial) => cartSpecial.dataset.productId === productId
    )

    if (cartSpecial) {
      cartSpecial.remove()
      const removedCartSpecials = sessionStorage.getItem('removedCartSpecials')

      sessionStorage.setItem(
        'removedCartSpecials',
        removedCartSpecials ? `${removedCartSpecials},${productId}` : productId
      )
    }

    this.recountCartSpecials()
  }

  recountCartSpecials() {
    const remainingCartSpecials = this.querySelectorAll(cartSpecialSelector)
    const remainingCartSpecialsCount = remainingCartSpecials.length

    if (remainingCartSpecialsCount === 0) {
      const cartSpecialsWrapper = this.querySelector(
        cartSpecialsWrapperSelector
      )
      cartSpecialsWrapper?.remove()
    }
  }

  updated() {
    if (
      !!this.cartSections[this.sectionId] &&
      JSON.stringify(this.cartSections[this.sectionId]) !==
        CartSections.SECTIONS_CACHE[this.sectionId]
    ) {
      this.$htmlUpdateUtility.reRunScripts(this)

      CartSections.SECTIONS_CACHE[this.sectionId] = JSON.stringify(
        this.cartSections[this.sectionId]
      )
      const html = this.$htmlUpdateUtility.parseSectionStringForSelector(
        this.cartSections[this.sectionId],
        `[section-id="${this.sectionId}"]`
      )

      html && this.$htmlUpdateUtility.replace(this, html)
      this.tiers?.length && this.handleCartTiers()
      setTimeout(() => {
        this.setupCartSpecials()

        // Initialize media-slider after cart content is updated
        if (this.querySelector('media-slider')) {
          if (customElements.get('media-slider')) {
            // Already defined, just call connectedCallback
            this.querySelectorAll('media-slider').forEach((slider) => {
              if (slider.connectedCallback && !slider.hasInitialized) {
                slider.connectedCallback()
              }
            })
          } else {
            // Dynamically import the media-slider module
            import('@theme/media-slider').then(() => {
              customElements.whenDefined('media-slider').then(() => {
                this.querySelectorAll('media-slider').forEach((slider) => {
                  if (slider.connectedCallback && !slider.hasInitialized) {
                    slider.connectedCallback()
                  }
                })
              })
            })
          }
        }
      }, 100)
    }
  }
}

customElements.define('cart-sections', CartSections)
